/**
 * A single clause row: ref picker → op picker → value input → delete.
 *
 * The row is self-contained: it takes a ClauseNode + an AvailableRef list
 * and emits a new ClauseNode via onChange. It never talks to the API.
 */

import { useMemo, useState, useCallback } from 'react'
import { Trash2 } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  SearchableSelect,
  type SearchableSelectGroup,
} from '@/shared/components/ui/searchable-select'
import { DatePicker } from '@/shared/components/ui/date-picker'
import {
  CATEGORY_LABELS,
  COUNT_OPS,
  OP_LABELS,
  VALUELESS_OPS,
  opsForType,
  type AvailableRef,
  type ClauseNode,
  type Op,
  type RefCategory,
} from './types'

interface ConditionRowProps {
  clause: ClauseNode
  availableRefs: AvailableRef[]
  byRef: (ref: string) => AvailableRef | undefined
  onChange: (next: ClauseNode) => void
  onRemove: () => void
  canRemove: boolean
}

export function ConditionRow({
  clause,
  availableRefs,
  byRef,
  onChange,
  onRemove,
  canRemove,
}: ConditionRowProps) {
  const activeRef = byRef(clause.ref)
  const refType = activeRef?.type ?? 'string'
  const allowedOps = useMemo(() => opsForType(refType), [refType])

  const refGroups = useMemo<SearchableSelectGroup[]>(() => {
    const byCat = new Map<RefCategory, AvailableRef[]>()
    for (const r of availableRefs) {
      if (!byCat.has(r.category)) byCat.set(r.category, [])
      byCat.get(r.category)!.push(r)
    }
    return [...byCat.entries()].map(([category, refs]) => ({
      label: CATEGORY_LABELS[category],
      options: refs.map((r) => ({ value: r.ref, label: r.label })),
    }))
  }, [availableRefs])

  function handleRefChange(nextRefRaw: unknown) {
    const nextRef = typeof nextRefRaw === 'string' ? nextRefRaw : ''
    const nextMeta = byRef(nextRef)
    const nextAllowed = nextMeta ? opsForType(nextMeta.type) : allowedOps
    const nextOp: Op = nextAllowed.includes(clause.op) ? clause.op : nextAllowed[0]
    onChange({ ...clause, ref: nextRef, op: nextOp, value: '' })
  }

  function handleOpChange(nextOpRaw: unknown) {
    const nextOp = (typeof nextOpRaw === 'string' ? nextOpRaw : 'eq') as Op
    onChange({
      ...clause,
      op: nextOp,
      value: VALUELESS_OPS.has(nextOp) ? undefined : (clause.value ?? ''),
    })
  }

  function handleValueChange(value: unknown) {
    onChange({ ...clause, value })
  }

  return (
    <div
      data-testid="condition-row"
      className="flex flex-wrap items-start gap-2 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-2"
    >
      <div className="min-w-[180px] flex-[1.4_1_240px]">
        <SearchableSelect
          value={clause.ref}
          onValueChange={handleRefChange}
          groups={refGroups}
          placeholder="Select a field…"
          searchPlaceholder="Search fields…"
          data-testid="condition-ref-trigger"
          triggerClassName="h-8 w-full text-[12px]"
        />
      </div>

      <div className="w-[160px] shrink-0">
        <Select value={clause.op} onValueChange={handleOpChange}>
          <SelectTrigger
            data-testid="condition-op-trigger"
            className="h-8 w-full text-[12px]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {allowedOps.map((op) => (
              <SelectItem key={op} value={op}>
                {OP_LABELS[op]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="min-w-[180px] flex-[1_1_240px]">
        <ValueInput
          clause={clause}
          refMeta={activeRef}
          onChange={handleValueChange}
        />
      </div>

      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        onClick={onRemove}
        disabled={!canRemove}
        data-testid="condition-remove"
        className="shrink-0 self-center text-[var(--ink-3)] disabled:opacity-30"
        title="Remove condition"
      >
        <Trash2 size={14} />
      </Button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Value input - picks its widget from the ref's inferred type + op shape.
// ---------------------------------------------------------------------------

interface ValueInputProps {
  clause: ClauseNode
  refMeta: AvailableRef | undefined
  onChange: (value: unknown) => void
}

function ValueInput({ clause, refMeta, onChange }: ValueInputProps) {
  if (VALUELESS_OPS.has(clause.op)) {
    return (
      <Input
        disabled
        placeholder="(no value required)"
        className="h-8 text-[12px]"
      />
    )
  }

  const type = refMeta?.type ?? 'string'

  if (COUNT_OPS.has(clause.op)) {
    if (clause.op === 'count_between') {
      const tuple = Array.isArray(clause.value) ? clause.value : ['', '']
      return (
        <div className="flex flex-wrap items-center gap-1.5">
          <Input
            data-testid="condition-value-input"
            type="number"
            min={0}
            value={String(tuple[0] ?? '')}
            onChange={(e) =>
              onChange([
                e.target.value === '' ? '' : Number(e.target.value),
                tuple[1] ?? '',
              ])
            }
            className="h-8 min-w-[80px] flex-1 text-[12px]"
          />
          <span className="text-[11px] text-[var(--ink-3)]">and</span>
          <Input
            type="number"
            min={0}
            value={String(tuple[1] ?? '')}
            onChange={(e) =>
              onChange([
                tuple[0] ?? '',
                e.target.value === '' ? '' : Number(e.target.value),
              ])
            }
            className="h-8 min-w-[80px] flex-1 text-[12px]"
          />
        </div>
      )
    }
    return (
      <Input
        data-testid="condition-value-input"
        type="number"
        min={0}
        value={clause.value == null ? '' : String(clause.value)}
        onChange={(e) =>
          onChange(e.target.value === '' ? '' : Number(e.target.value))
        }
        placeholder="Count"
        className="h-8 text-[12px]"
      />
    )
  }

  if (clause.op === 'in' || clause.op === 'not_in') {
    const options = refMeta?.options ?? []
    const list = Array.isArray(clause.value)
      ? clause.value
      : clause.value
        ? [clause.value]
        : []
    if (options.length > 0) {
      const selected = String(list[0] ?? '')
      return (
        <Select
          value={selected}
          onValueChange={(next) => onChange([next])}
        >
          <SelectTrigger
            data-testid="condition-value-trigger"
            className="h-8 w-full text-[12px]"
          >
            <SelectValue placeholder="Select…" />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )
    }
    return (
      <Input
        data-testid="condition-value-input"
        value={list.join(', ')}
        placeholder="comma-separated values"
        onChange={(e) =>
          onChange(
            e.target.value
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean),
          )
        }
        className="h-8 text-[12px]"
      />
    )
  }

  if (clause.op === 'between') {
    const tuple = Array.isArray(clause.value) ? clause.value : ['', '']
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <Input
          data-testid="condition-value-input"
          type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
          value={String(tuple[0] ?? '')}
          onChange={(e) => onChange([e.target.value, tuple[1] ?? ''])}
          className="h-8 min-w-[120px] flex-1 text-[12px]"
        />
        <span className="text-[11px] text-[var(--ink-3)]">and</span>
        <Input
          type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
          value={String(tuple[1] ?? '')}
          onChange={(e) => onChange([tuple[0] ?? '', e.target.value])}
          className="h-8 min-w-[120px] flex-1 text-[12px]"
        />
      </div>
    )
  }

  if (type === 'enum') {
    return (
      <Select value={String(clause.value ?? '')} onValueChange={onChange}>
        <SelectTrigger
          data-testid="condition-value-trigger"
          className="h-8 w-full text-[12px]"
        >
          <SelectValue placeholder="Select…" />
        </SelectTrigger>
        <SelectContent>
          {(refMeta?.options ?? []).map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  if (type === 'boolean') {
    return (
      <Select
        value={clause.value === true ? 'true' : clause.value === false ? 'false' : ''}
        onValueChange={(v) => onChange(v === 'true')}
      >
        <SelectTrigger
          data-testid="condition-value-trigger"
          className="h-8 w-full text-[12px]"
        >
          <SelectValue placeholder="true / false" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="true">true</SelectItem>
          <SelectItem value="false">false</SelectItem>
        </SelectContent>
      </Select>
    )
  }

  if (type === 'date') {
    return <DateValueInput value={clause.value} onChange={onChange} />
  }

  return (
    <Input
      data-testid="condition-value-input"
      type={type === 'number' ? 'number' : 'text'}
      value={clause.value == null ? '' : String(clause.value)}
      onChange={(e) =>
        onChange(type === 'number' ? Number(e.target.value) : e.target.value)
      }
      placeholder="Value"
      className="h-8 text-[12px]"
    />
  )
}

// ---------------------------------------------------------------------------
// Date value input - toggle between a specific date and a relative period.
//
// Relative periods are serialised as `now+Nd`, `now-Nw`, `now+NM`, `now-Ny`
// (d=days, w=weeks, M=months, y=years) and resolved server-side at eval time.
// ---------------------------------------------------------------------------

const RELATIVE_DATE_RE = /^now\s*([+-])\s*(\d+)\s*([dwMy])$/

type PeriodUnit = 'd' | 'w' | 'M' | 'y'

const UNIT_LABELS: Record<PeriodUnit, string> = {
  d: 'days',
  w: 'weeks',
  M: 'months',
  y: 'years',
}

function parseRelative(v: unknown): {
  sign: '+' | '-'
  amount: number
  unit: PeriodUnit
} | null {
  if (typeof v !== 'string') return null
  const m = RELATIVE_DATE_RE.exec(v)
  if (!m) return null
  return {
    sign: m[1] as '+' | '-',
    amount: Number(m[2]),
    unit: m[3] as PeriodUnit,
  }
}

function DateValueInput({
  value,
  onChange,
}: {
  value: unknown
  onChange: (v: string) => void
}) {
  const parsed = parseRelative(value)
  const isRelative = parsed !== null
  const [mode, setMode] = useState<'specific' | 'relative'>(
    isRelative ? 'relative' : 'specific',
  )

  const handleModeChange = useCallback(
    (next: string) => {
      const m = next as 'specific' | 'relative'
      setMode(m)
      onChange(m === 'relative' ? 'now-6M' : '')
    },
    [onChange],
  )

  const handleAmountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const p = parseRelative(value) ?? { sign: '-', amount: 6, unit: 'M' as PeriodUnit }
      const num = Math.max(0, Number(e.target.value) || 0)
      onChange(`now${p.sign}${num}${p.unit}`)
    },
    [value, onChange],
  )

  const handleUnitChange = useCallback(
    (u: string) => {
      const p = parseRelative(value) ?? { sign: '-', amount: 6, unit: 'M' as PeriodUnit }
      onChange(`now${p.sign}${p.amount}${u}`)
    },
    [value, onChange],
  )

  const handleSignChange = useCallback(
    (s: string) => {
      const p = parseRelative(value) ?? { sign: '-', amount: 6, unit: 'M' as PeriodUnit }
      onChange(`now${s}${p.amount}${p.unit}`)
    },
    [value, onChange],
  )

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Select value={mode} onValueChange={handleModeChange}>
        <SelectTrigger className="h-8 w-[100px] shrink-0 text-[11px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="specific">Exact date</SelectItem>
          <SelectItem value="relative">Period</SelectItem>
        </SelectContent>
      </Select>

      {mode === 'specific' ? (
        <DatePicker
          value={typeof value === 'string' && !isRelative ? value : ''}
          onChange={onChange}
          className="min-w-[160px] flex-1"
        />
      ) : (
        <>
          <Select
            value={parsed?.sign ?? '-'}
            onValueChange={handleSignChange}
          >
            <SelectTrigger className="h-8 w-[70px] shrink-0 text-[11px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="-">ago</SelectItem>
              <SelectItem value="+">from now</SelectItem>
            </SelectContent>
          </Select>
          <Input
            type="number"
            min={0}
            value={parsed?.amount ?? 6}
            onChange={handleAmountChange}
            className="h-8 w-[60px] shrink-0 text-[12px]"
          />
          <Select
            value={parsed?.unit ?? 'M'}
            onValueChange={handleUnitChange}
          >
            <SelectTrigger className="h-8 w-[90px] shrink-0 text-[11px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.entries(UNIT_LABELS) as [PeriodUnit, string][]).map(
                ([u, label]) => (
                  <SelectItem key={u} value={u}>
                    {label}
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
        </>
      )}
    </div>
  )
}
